import React, { useEffect, useState } from 'react';
import { Copy, Check, Mail, Download, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/lib/api';

const T = {
  es: { title: 'Compartir', copy: 'Copiar enlace', copied: '¡Enlace copiado!', qr: 'Código QR', download: 'Descargar PNG', hint: 'Imprímelo para tus huéspedes', email: 'Correo', loading: 'Preparando enlace…' },
  en: { title: 'Share', copy: 'Copy link', copied: 'Link copied!', qr: 'QR code', download: 'Download PNG', hint: 'Print it for your guests', email: 'Email', loading: 'Preparing link…' },
  de: { title: 'Teilen', copy: 'Link kopieren', copied: 'Link kopiert!', qr: 'QR-Code', download: 'PNG herunterladen', hint: 'Zum Ausdrucken für Gäste', email: 'E-Mail', loading: 'Link wird vorbereitet…' },
};

const WhatsAppIcon = (p) => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" /></svg>;
const TelegramIcon = (p) => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M21.9 4.6 18.7 19.7c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.4-5 9.1-8.2c.4-.4-.1-.5-.6-.2L6.2 13.4 1.4 11.9c-1-.3-1.1-1 .2-1.5L20.5 3.1c.9-.3 1.6.2 1.4 1.5z" /></svg>;
const XIcon = (p) => <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...p}><path d="M18.2 2h3.4l-7.4 8.5L23 22h-6.8l-5.3-7-6.1 7H1.4l7.9-9.1L1 2h7l4.8 6.4L18.2 2zm-1.2 18h1.9L7.1 3.9H5.1L17 20z" /></svg>;
const FacebookIcon = (p) => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z" /></svg>;

const networks = (url, text) => [
  { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon, color: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}` },
  { key: 'telegram', label: 'Telegram', Icon: TelegramIcon, color: '#229ED9', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  { key: 'x', label: 'X', Icon: XIcon, color: '#000000', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon, color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
];

export const ShareDialog = ({ open, onOpenChange, kind, itemId, language = 'es', onShared }) => {
  const t = T[language] || T.es;
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLink(null); setCopied(false);
    api.getShareLink(kind, itemId, language).then((r) => setLink(r.data)).catch(() => setLink(false));
  }, [open, kind, itemId, language]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); } catch { /* ignore */ }
    setCopied(true); onShared?.('copy');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${link.qr_png}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `qr-${(link.title || 'calma-guide').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      onShared?.('qr');
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 rounded-none sm:rounded-none overflow-hidden" data-testid="share-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle className="text-base">{t.title}</DialogTitle>
          <DialogDescription className="truncate">{link?.title || ''}</DialogDescription>
        </DialogHeader>
        {link === null && <div className="p-10 flex justify-center text-gray-400 text-sm gap-2"><Loader2 className="animate-spin" size={18} /> {t.loading}</div>}
        {link === false && <p className="p-6 text-sm text-gray-500">—</p>}
        {link && (
          <>
            <div className="px-5 pb-4">
              <button onClick={copy} className={`w-full flex items-center gap-3 px-4 py-3 border transition-colors duration-200 ${copied ? 'border-[#38B2AC] bg-[#38B2AC]/5 text-[#1D1D1F]' : 'border-gray-200 hover:border-[#38B2AC]'}`} data-testid="share-copy-btn">
                {copied ? <Check size={18} className="text-[#38B2AC]" /> : <Copy size={18} className="text-gray-500" />}
                <span className="text-sm font-semibold">{copied ? t.copied : t.copy}</span>
                <span className="ml-auto text-xs text-gray-400 truncate max-w-[45%]">{link.url.replace('https://', '')}</span>
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1 px-5 pb-5">
              {networks(link.url, link.text).map(({ key, label, Icon, color, href }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer" onClick={() => onShared?.(key)}
                  className="flex flex-col items-center gap-1.5 py-2 text-gray-500 hover:text-[var(--c)] transition-colors duration-200" style={{ '--c': color }} data-testid={`share-${key}`}>
                  <Icon /> <span className="text-[11px]">{label}</span>
                </a>
              ))}
              <a href={`mailto:?subject=${encodeURIComponent(link.title)}&body=${encodeURIComponent(`${link.text}\n${link.url}`)}`} onClick={() => onShared?.('email')}
                className="flex flex-col items-center gap-1.5 py-2 text-gray-500 hover:text-[#38B2AC] transition-colors duration-200" data-testid="share-email">
                <Mail size={22} /> <span className="text-[11px]">{t.email}</span>
              </a>
            </div>
            <div className="bg-[#FDFBF7] border-t border-gray-100 px-5 py-4 flex items-center gap-4">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${link.qr_png}`} alt="QR" className="w-24 h-24 bg-white border border-gray-100" data-testid="share-qr-img" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#1D1D1F]">{t.qr}</p>
                <p className="text-xs text-gray-500 mb-2">{t.hint}</p>
                <button onClick={downloadQr} disabled={downloading} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#38B2AC] hover:text-[#2C8F8A]" data-testid="share-qr-download">
                  {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {t.download}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
